import { schoolsApi } from "../utils/apiService";

class DistrictCacheService {
    constructor() {
      this.DB_NAME = 'districtDataCache';
      this.DB_VERSION = 2; // Increment version to clear corrupted cache from UUID migration
      this.OPEN_TIMEOUT = 5000; // Upper bound on indexedDB.open before giving up
      this.STORES = {
        metrics: 'metrics',
        teamAccess: 'teamAccess'
      };
    }
  
    async initialize() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        // A version bump makes onblocked likely: any tab still holding the
        // previous version stops this open from ever completing, and onblocked
        // is followed by neither onsuccess nor onerror.
        let settled = false;
        const finish = (fn, arg) => {
          if (settled) return;
          settled = true;
          fn(arg);
        };

        const openTimeout = setTimeout(
          () => finish(reject, new Error('IndexedDB open timed out')),
          this.OPEN_TIMEOUT
        );
        const done = (fn, arg) => {
          clearTimeout(openTimeout);
          finish(fn, arg);
        };

        request.onerror = () => done(reject, request.error);
        request.onblocked = () => done(reject, new Error('IndexedDB open blocked'));
        request.onsuccess = () => {
          this.db = request.result;
          done(resolve);
        };
  
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          
          // Delete old stores if they exist (for schema migration)
          if (db.objectStoreNames.contains(this.STORES.metrics)) {
            db.deleteObjectStore(this.STORES.metrics);
          }
          
          // Create new metrics store with hybrid ID support (UUID preferred, NCES fallback)
          const metricsStore = db.createObjectStore(this.STORES.metrics, { keyPath: 'id' });
          metricsStore.createIndex('by_team', 'teams', { multiEntry: true });
          metricsStore.createIndex('by_uuid', 'uuid', { unique: false });
          metricsStore.createIndex('by_ncessch', 'ncessch', { unique: false });
  
          if (!db.objectStoreNames.contains(this.STORES.teamAccess)) {
            const teamStore = db.createObjectStore(this.STORES.teamAccess, { keyPath: 'teamId' });
            teamStore.createIndex('by_version', 'version');
          }
        };
      });
    }
  
      async cacheDistrictData(districtData, teamId) {
    if (!districtData?.metrics || !teamId) {
      console.warn('Invalid district data or team ID for caching');
      return;
    }

    const tx = this.db.transaction(Object.values(this.STORES), 'readwrite');
    const metricsStore = tx.objectStore(this.STORES.metrics);
    const teamStore = tx.objectStore(this.STORES.teamAccess);

    return new Promise((resolve, reject) => {
      // First, get all existing metrics for the team
      const existingMetrics = new Set();
      
      districtData.metrics.forEach(metric => {
        // Create a hybrid ID: prefer UUID, fallback to NCES ID, or generate one
        const hybridId = metric.uuid || metric.ncessch || `unknown-${Date.now()}-${Math.random()}`;
        
        const existingDataRequest = metricsStore.get(hybridId);
        
        existingDataRequest.onsuccess = () => {
          const existingData = existingDataRequest.result;
          
          // Create or update the teams array
          const teams = new Set(existingData?.teams || []);
          teams.add(teamId); // Add the current team ID

          // Prepare the metric with team association and hybrid ID support
          const updatedMetric = {
            ...metric,
            id: hybridId, // PRIMARY KEY - Hybrid UUID/NCES ID
            uuid: metric.uuid, // UUID identifier (may be null for legacy schools)
            ncessch: metric.ncessch, // NCES identifier (may be null for some schools)
            teams: Array.from(teams),
            lastUpdated: new Date().toISOString(),
            version: districtData.version || '2.0',
            // Ensure required nested objects exist with new structure
            population: metric.population || {
              status: 'unknown',
              projection_status: 'unknown',
              current: null,
              past: null,
              future: null
            },
            enrollment: metric.enrollment || {
              status: 'unknown',
              projection_status: 'unknown',
              current: null,
              past: null,
              projected: null
            },
            market_share: metric.market_share || {
              status: 'unknown',
              current: null,
              past: null
            }
          };

          existingMetrics.add(hybridId);
          metricsStore.put(updatedMetric);
        };
          });
    
          // Update team metadata
          const teamData = {
            teamId,
            version: districtData.version || '1.0',
            lastUpdated: new Date().toISOString(),
            schoolCount: districtData.metrics.length,
            metricIds: Array.from(existingMetrics)
          };
          teamStore.put(teamData);
    
          tx.oncomplete = () => {
            resolve();
          };
          tx.onerror = () => reject(tx.error);
        });
      }
  
    async getTeamMetrics(teamId) {
      if (!this.db) {
        await this.initialize();
      }
  
      if (!teamId) return [];
        
      const tx = this.db.transaction([this.STORES.metrics], 'readonly');
      const metricsStore = tx.objectStore(this.STORES.metrics);
      const teamIndex = metricsStore.index('by_team');
  
      return new Promise((resolve, reject) => {
        const request = teamIndex.getAll(teamId);
  
        request.onsuccess = () => {
          const metrics = request.result;
          // Ensure each metric has the required nested objects
          const processedMetrics = metrics.map(metric => ({
            ...metric,
            population: metric.population || {
              status: 'unknown',
              current: null,
              past: null
            },
            enrollment: metric.enrollment || {
              status: 'unknown',
              current: null,
              past: null
            },
            market_share: metric.market_share || {
              status: 'unknown',
              current: null,
              past: null
            }
          }));
          resolve(processedMetrics);
        };
  
        request.onerror = () => reject(request.error);
      });
    }
  
    async getMetadata(teamId) {
      if (!this.db) {
        await this.initialize();
      }
  
      if (!teamId) return null;
  
      const tx = this.db.transaction([this.STORES.teamAccess], 'readonly');
      const teamStore = tx.objectStore(this.STORES.teamAccess);
  
      return new Promise((resolve) => {
        const request = teamStore.get(teamId);
        
        request.onsuccess = () => {
          resolve(request.result || null);
        };
  
        request.onerror = () => resolve(null);
      });
    }
  
    async clearTeamCache(teamId) {
      const tx = this.db.transaction(Object.values(this.STORES), 'readwrite');
      const metricsStore = tx.objectStore(this.STORES.metrics);
      const teamStore = tx.objectStore(this.STORES.teamAccess);
  
      return new Promise((resolve, reject) => {
        const teamIndex = metricsStore.index('by_team');
        const request = teamIndex.getAll(teamId);
  
        request.onsuccess = () => {
          const metrics = request.result;
          
          // Remove team from metrics
          metrics.forEach(metric => {
            const teams = metric.teams.filter(t => t !== teamId);
            if (teams.length === 0) {
              metricsStore.delete(metric.ncessch);
            } else {
              metricsStore.put({ ...metric, teams });
            }
          });
  
          // Remove team access record
          teamStore.delete(teamId);
        };
  
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }

    async clearAllMetrics() {
      /**
       * Clear all metrics data without deleting the database.
       * This works even when other tabs have the database open.
       * If database doesn't exist yet, silently skips (nothing to clear).
       */
      try {
        // Try to check if database exists (may not be supported in all browsers)
        try {
          const databases = await indexedDB.databases();
          const dbExists = databases.some(db => db.name === this.DB_NAME);
          
          if (!dbExists) {
            console.log('District metrics database does not exist yet, nothing to clear');
            return;
          }
        } catch (e) {
          // indexedDB.databases() not supported, continue anyway
          console.debug('indexedDB.databases() not supported, attempting to clear cache anyway');
        }

        if (!this.db) {
          await this.initialize();
        }

        // Double-check database is ready
        if (!this.db) {
          console.warn('Database not initialized, skipping cache clear');
          return;
        }

        // Try to create transaction - if stores don't exist, this will fail
        let tx;
        try {
          tx = this.db.transaction(Object.values(this.STORES), 'readwrite');
        } catch (txError) {
          // Transaction creation failed - likely wrong schema or stores don't exist
          console.log('Unable to create transaction (stores may not exist), skipping cache clear');
          return;
        }

        const metricsStore = tx.objectStore(this.STORES.metrics);
        const teamStore = tx.objectStore(this.STORES.teamAccess);
        const metadataStore = tx.objectStore(this.STORES.metadata);

        return new Promise((resolve, reject) => {
          // Clear all object stores
          metricsStore.clear();
          teamStore.clear();
          metadataStore.clear();

          tx.oncomplete = () => {
            console.log('All district metrics cleared successfully');
            resolve();
          };
          tx.onerror = () => {
            console.error('Error clearing district metrics:', tx.error);
            reject(tx.error);
          };
        });
      } catch (error) {
        // Catch any other errors (like objectStore not found)
        // Don't throw - gracefully handle the error
        return;
      }
    }

    async prefetchDistrictData(teamId = 'base') {
        try {
          // Initialize if not already done
          if (!this.db) {
            await this.initialize();
          }
    
          // Check if we already have fresh data
          const metadata = await this.getMetadata(teamId);
          if (metadata) {
            const lastUpdated = new Date(metadata.lastUpdated);
            const now = new Date();
            const hoursSinceUpdate = (now - lastUpdated) / (1000 * 60 * 60);
            
            // If data is fresh (less than 24 hours old), skip prefetch
            if (hoursSinceUpdate < 24) {
              return;
            }
          }
    
          // Fetch new data silently
          const response = await schoolsApi.getDistrictMetrics();
          
          if (response?.metrics?.length > 0) {
            await this.cacheDistrictData(response, teamId);
          }
        } catch (error) {
          // Log but don't throw prefetch errors
          console.error('District data prefetch error:', error);
        }
      }
    
      // Add method to check if we have fresh data
      async hasFreshData(teamId = 'base') {
        if (!this.db) {
          await this.initialize();
        }
    
        const metadata = await this.getMetadata(teamId);
        if (!metadata) return false;
    
        const lastUpdated = new Date(metadata.lastUpdated);
        const now = new Date();
        const hoursSinceUpdate = (now - lastUpdated) / (1000 * 60 * 60);
        
        return hoursSinceUpdate < 24;
      }
  
      async clearAllCache() {
        try {
          // Initialize if not already done
          if (!this.db) {
            await this.initialize();
          }
      
          const tx = this.db.transaction(Object.values(this.STORES), 'readwrite');
          
          return Promise.all(
            Object.values(this.STORES).map(
              storeName => new Promise((resolve, reject) => {
                const store = tx.objectStore(storeName);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
              })
            )
          );
        } catch (error) {
          console.error('Error clearing cache:', error);
          // Optionally rethrow if you want to handle it in the logout flow
          throw error;
        }
      }

      // Method to completely delete and recreate the database (for corrupted cache issues)
      async deleteDatabaseAndRecreate() {
        try {
          if (this.db) {
            this.db.close();
            this.db = null;
          }
          
          return new Promise((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(this.DB_NAME);
            
            deleteRequest.onsuccess = () => {
              console.log('District cache database deleted successfully');
              resolve();
            };
            
            deleteRequest.onerror = () => {
              console.error('Error deleting district cache database:', deleteRequest.error);
              reject(deleteRequest.error);
            };
            
            // Blocked deletion otherwise leaves this promise pending forever,
            // since no further event follows.
            deleteRequest.onblocked = () => {
              console.warn('Database deletion blocked - try closing other tabs');
              reject(new Error('IndexedDB deletion blocked'));
            };
          });
        } catch (error) {
          console.error('Error deleting district cache database:', error);
          throw error;
        }
      }
  }
  
  export const districtCache = new DistrictCacheService();

// Debug utility - can be called from browser console
window.clearDistrictCache = async () => {
  try {
    await districtCache.deleteDatabaseAndRecreate();
    console.log('✅ District cache cleared! Please refresh the page.');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear district cache:', error);
    return false;
  }
};

// Debug utility to inspect cached data
window.inspectDistrictCache = async () => {
  try {
    await districtCache.initialize();
    const teamMetrics = await districtCache.getTeamMetrics('base');
    console.log('📊 Cached district metrics:', teamMetrics);
    console.log(`Total schools: ${teamMetrics.length}`);
    console.log(`Schools with UUIDs: ${teamMetrics.filter(m => m.uuid).length}`);
    console.log(`Schools with NCES IDs: ${teamMetrics.filter(m => m.ncessch).length}`);
    console.log(`Schools with both: ${teamMetrics.filter(m => m.uuid && m.ncessch).length}`);
    return teamMetrics;
  } catch (error) {
    console.error('❌ Failed to inspect district cache:', error);
    return null;
  }
};